import { type Editor, useEditorState } from '@tiptap/react';
import { useCallback } from 'react';
import {
  type BubbleMenuShouldShowArgs,
  bubbleMenuTextShouldShow,
} from './bubbleMenuTextShouldShow';

type FormatStateCtx = { editor: Editor };

/**
 * Shared BubbleMenu text toolbar: `useEditorState` for format state + safe `shouldShow`.
 * Used by v1 / vMarkdown (full) and vJSON (minimal) `useTextMenuStates` wrappers.
 * Values are usually toggle booleans, but may be any derived format state (e.g.
 * markdown-native's active heading level), so the constraint is `unknown`.
 */
export function useBubbleTextMenuStates<T extends Record<string, unknown>>({
  editor,
  selector,
  selectFormatStates,
}: {
  editor: Editor;
  selector: string;
  selectFormatStates: (ctx: FormatStateCtx) => T;
}) {
  const states = useEditorState({
    editor,
    selector: selectFormatStates,
  });

  const shouldShow = useCallback(
    (props: BubbleMenuShouldShowArgs) =>
      bubbleMenuTextShouldShow(editor, selector, { view: props.view, from: props.from }),
    [editor, selector]
  );

  return {
    ...states,
    shouldShow,
  };
}
